import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Clock,
  FileText,
  PencilLine,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Timer,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import { ensureWeeklyPlan, regenerateWeeklyPlan } from "@/tenants/einstein/features/study-plan/api";
import { disciplineLabel } from "@/tenants/einstein/features/study-plan/algorithm";
import type { BlockKind, StudyBlock, StudyPlan } from "@/tenants/einstein/features/study-plan/types";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";

const BLOCK_ICONS: Record<BlockKind, LucideIcon> = {
  study: BookOpen,
  review: RotateCcw,
  essay: PencilLine,
  discursive: FileText,
  simulation: Timer,
};

const BLOCK_LABEL: Record<BlockKind, string> = {
  study: "Estudo",
  review: "Revisão",
  essay: "Redação",
  discursive: "Discursiva",
  simulation: "Simulado",
};

const BLOCK_TONE: Record<BlockKind, Tone> = {
  study: "violet",
  review: "coral",
  essay: "yellow",
  discursive: "mint",
  simulation: "sky",
};

const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

const TONE_BADGE: Record<Tone, "accent" | "pink" | "mint" | "yellow" | "coral" | "sky"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

const WEEKDAY_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export function StudyPlanPage() {
  const { session } = useSession();
  const { profile } = useProfile();
  const [plan, setPlan] = useState<StudyPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!session || !profile) return;
    setLoading(true);
    ensureWeeklyPlan({
      userId: session.user.id,
      weeklyHours: profile.weekly_hours ?? 10,
      examDate: profile.exam_date,
    })
      .then(setPlan)
      .catch((err) => toast.error(err instanceof Error ? err.message : "Falha ao carregar plano."))
      .finally(() => setLoading(false));
  }, [session, profile]);

  async function handleRegenerate() {
    if (!session || !profile) return;
    setRegenerating(true);
    try {
      const p = await regenerateWeeklyPlan({
        userId: session.user.id,
        weeklyHours: profile.weekly_hours ?? 10,
        examDate: profile.exam_date,
      });
      setPlan(p);
      toast.success("Plano regenerado.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao regenerar.");
    } finally {
      setRegenerating(false);
    }
  }

  if (loading || !plan) {
    return (
      <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
        <Container size="wide" className="py-20">
          <Badge variant="accentSolid">Plano da semana</Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold">
            Calibrando seu plano…
          </h1>
        </Container>
      </div>
    );
  }

  const totalWeekMin = plan.days.reduce((acc, d) => acc + d.total_min, 0);
  const startDate = parseISO(plan.week_start);
  const endDate = parseISO(plan.days.at(-1)?.date ?? plan.week_start);

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-16 md:py-20">
        {/* HERO */}
        <header className="animate-fade-up grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <Badge variant="accentSolid">
              <Sparkles className="size-3" aria-hidden />
              {format(startDate, "d 'de' MMM", { locale: ptBR })} —{" "}
              {format(endDate, "d 'de' MMM", { locale: ptBR })}
            </Badge>
            <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Sua <span className="text-gradient-accent">semana.</span>
            </h1>
            <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Plano gerado a partir do diagnóstico e do tempo disponível.
            </p>
          </div>
          <Button
            onClick={handleRegenerate}
            loading={regenerating}
            variant="secondary"
            size="lg"
            className="md:self-end"
          >
            <RefreshCw className="size-4" aria-hidden />
            Regenerar plano
          </Button>
        </header>

        {/* METRICS */}
        <section className="mt-10 grid gap-5 md:grid-cols-2">
          <MetricLarge
            tone="violet"
            label="Volume planejado"
            value={`${Math.round(totalWeekMin / 60)}h`}
            sub={`Meta: ${plan.weekly_hours_target}h/semana`}
          />
          <MetricLarge
            tone="mint"
            label="Ganho marginal estimado"
            value={`+${plan.marginal_gain_estimate.toFixed(1)} pt`}
            sub="Estimativa otimista da semana"
          />
        </section>

        {/* Como pensei */}
        {plan.notes.length > 0 ? (
          <section className="mt-8 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
            <div className="flex items-center gap-3">
              <span className="chip size-9 bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)]">
                <Sparkles className="size-4" aria-hidden />
              </span>
              <p className="font-display text-[length:var(--text-h4)] font-semibold">
                Como pensei o plano
              </p>
            </div>
            <ul className="mt-4 space-y-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              {plan.notes.map((n, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-accent)] shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* DIAS */}
        <section className="mt-10 space-y-5">
          {plan.days.map((d, i) => (
            <DayCard
              key={d.date}
              date={d.date}
              weekday={d.weekday}
              blocks={d.blocks}
              totalMin={d.total_min}
              delay={i * 50}
            />
          ))}
        </section>
      </Container>
    </div>
  );
}

function MetricLarge({
  tone,
  label,
  value,
  sub,
}: {
  tone: Tone;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="lift rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
      <Badge variant={TONE_BADGE[tone]}>{label}</Badge>
      <p
        data-tabular="true"
        className="mt-5 font-display text-[length:var(--text-display-md)] font-bold leading-none tracking-[var(--text-display-md--letter-spacing)]"
      >
        {value}
      </p>
      <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">{sub}</p>
    </div>
  );
}

function DayCard({
  date,
  weekday,
  blocks,
  totalMin,
  delay,
}: {
  date: string;
  weekday: number;
  blocks: StudyBlock[];
  totalMin: number;
  delay: number;
}) {
  const isToday = date === new Date().toISOString().slice(0, 10);
  return (
    <article
      className={`animate-fade-up rounded-2xl border bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] ${
        isToday
          ? "border-[--color-accent] ring-4 ring-[--color-accent]/15"
          : "border-[--color-border]"
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Badge variant={isToday ? "accent" : "outline"}>
            {isToday ? "Hoje" : WEEKDAY_LABELS[weekday]}
          </Badge>
          <p className="mt-2 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)]">
            {format(parseISO(date), "d 'de' MMMM", { locale: ptBR })}
          </p>
        </div>
        <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          <span data-tabular="true" className="font-mono">
            {blocks.length}
          </span>{" "}
          bloco{blocks.length === 1 ? "" : "s"} ·{" "}
          <span data-tabular="true" className="font-mono">
            {totalMin}
          </span>{" "}
          min
        </p>
      </header>
      {blocks.length > 0 ? (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {blocks.map((b) => (
            <BlockCard key={b.id} block={b} />
          ))}
        </div>
      ) : (
        <p className="mt-5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Sem blocos — bom descanso.
        </p>
      )}
    </article>
  );
}

function BlockCard({ block }: { block: StudyBlock }) {
  const Icon = BLOCK_ICONS[block.kind];
  const tone = BLOCK_TONE[block.kind];
  const title = block.discipline ? disciplineLabel(block.discipline) : BLOCK_LABEL[block.kind];

  const content = (
    <>
      <div className="flex items-start justify-between">
        <span className={`chip size-10 shrink-0 ${TONE_BG[tone]}`}>
          <Icon className="size-4" aria-hidden />
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-paper)] px-2.5 py-1 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          <Clock className="size-3" aria-hidden />
          <span data-tabular="true" className="font-mono">
            {block.duration_min}
          </span>{" "}
          min
        </span>
      </div>
      <p className="mt-4 text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {BLOCK_LABEL[block.kind]}
      </p>
      <p className="mt-1 font-display text-[length:var(--text-h4)] font-semibold">{title}</p>
      <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)] text-pretty">
        {block.reason}
      </p>
      {block.link ? (
        <span className="mt-4 inline-flex items-center gap-1.5 text-[length:var(--text-caption)] font-semibold text-[color:var(--color-accent)]">
          Executar agora
          <ArrowUpRight className="size-3.5" aria-hidden />
        </span>
      ) : null}
    </>
  );

  if (block.link) {
    return (
      <Link
        to={block.link}
        className="lift block rounded-xl border border-[--color-border] bg-[color:var(--color-paper)] p-5 transition-colors hover:border-[--color-accent]/40 hover:bg-[color:var(--color-card)]"
      >
        {content}
      </Link>
    );
  }
  return (
    <div className="rounded-xl border border-[--color-border] bg-[color:var(--color-paper)] p-5">
      {content}
    </div>
  );
}
